import React from "react";

const TERP_COLORS = ["#9DFF00", "#4ADE80", "#C026D3", "#FF4B89"];

export const perUnit = (x) => x.price / Math.max(x.units || 1, 1);

export function TierSelector({ tiers, selTier, onSelect }) {
  const refUnit = perUnit(tiers[0]);
  const bestIdx = tiers.reduce((b, y, j) => (perUnit(y) < perUnit(tiers[b]) ? j : b), 0);
  return (
    <div data-testid="tier-selector">
      <div className="flex items-center justify-between mb-2">
        <p className="text-zinc-400 text-xs uppercase font-bold">Choisis ton format</p>
        <span className="text-neon-green text-[11px] uppercase font-bold">Tarifs dégressifs</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {tiers.map((x, ti) => {
          const pct = refUnit > 0 ? Math.round((1 - perUnit(x) / refUnit) * 100) : 0;
          const active = selTier?.label === x.label;
          const tag = ti === 1 ? "Populaire" : ti === bestIdx && ti > 1 ? "Économique" : null;
          return (
            <button
              key={x.label}
              type="button"
              data-testid="tier-option"
              onClick={() => onSelect(x)}
              className={`relative flex items-center justify-between gap-2 border px-3 py-2.5 text-left transition-colors ${active ? "border-neon-green bg-neon-green/10" : "border-zinc-700 hover:border-neon-green/60"}`}
            >
              {tag && <span className={`absolute -top-2 left-2 text-[9px] uppercase font-bold px-1.5 py-0.5 ${tag === "Populaire" ? "bg-neon-green text-black" : "bg-neon-magenta text-white"}`}>{tag}</span>}
              <span>
                <span className={`font-anton text-base block ${active ? "text-neon-green" : "text-white"}`}>{x.label}</span>
                {(x.units || 1) > 1 && <span className="text-zinc-500 text-[11px]">{perUnit(x).toFixed(2)}€/g</span>}
              </span>
              <span className="text-right">
                <span className="font-anton text-white block">{Number(x.price).toFixed(2)}€</span>
                {pct > 0 && <span className="text-neon-magenta text-[11px] font-bold">-{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TerpeneProfile({ terpenes, total }) {
  const top = terpenes.slice(0, 4);
  const sum = top.reduce((s, x) => s + (Number(x.pct) || 0), 0) || 1;
  const C = 2 * Math.PI * 40;
  let offset = 0;
  return (
    <div className="border border-zinc-800 bg-ink p-3" data-testid="modal-terpenes">
      <div className="flex items-center justify-between mb-2">
        <p className="text-neon-green text-xs uppercase font-bold">Profil terpénique & dominances</p>
        <span className="text-zinc-600 text-[10px] uppercase font-bold">Fingerprint phyto-chimique</span>
      </div>
      <div className="flex items-center gap-4">
        <svg viewBox="0 0 100 100" className="h-24 w-24 shrink-0 -rotate-90">
          <circle cx="50" cy="50" r="40" fill="none" stroke="#27272a" strokeWidth="10" />
          {top.map((x, i) => {
            const frac = (Number(x.pct) || 0) / sum;
            const seg = (
              <circle key={x.name} cx="50" cy="50" r="40" fill="none" stroke={TERP_COLORS[i % 4]} strokeWidth="10"
                strokeDasharray={`${frac * C - 2} ${C - frac * C + 2}`} strokeDashoffset={-offset * C} strokeLinecap="butt" />
            );
            offset += frac;
            return seg;
          })}
        </svg>
        <div className="flex-1 space-y-1.5">
          {total && <p className="text-white font-anton text-lg leading-none">{total} <span className="text-zinc-500 text-[10px] uppercase font-outfit font-bold">terpènes totaux</span></p>}
          {top.map((x, i) => (
            <div key={x.name} className="flex items-center gap-2 text-xs">
              <span className="h-2 w-2 rounded-full shrink-0" style={{ background: TERP_COLORS[i % 4] }} />
              <span className="text-white font-semibold">{x.name} ({x.pct}%)</span>
              {x.note && <span className="text-zinc-500 truncate">{x.note}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ConformityCard({ product }) {
  return (
    <div className="border border-zinc-800 bg-ink p-3" data-testid="modal-conformity">
      <div className="flex items-center justify-between mb-2">
        <p className="text-neon-green text-xs uppercase font-bold">Fiche d'identité & conformité</p>
        <span className="text-zinc-600 text-[10px] uppercase font-bold">Audit UE</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-ink-surface border border-zinc-800 p-2.5">
          <p className="text-zinc-500 text-[10px] uppercase font-bold">Concentration CBD</p>
          <p className="text-neon-green font-anton text-lg" data-testid="modal-cbd-rate">{product.cbd_rate || "—"}</p>
        </div>
        <div className="bg-ink-surface border border-zinc-800 p-2.5">
          <p className="text-zinc-500 text-[10px] uppercase font-bold">Taux de THC</p>
          <p className="text-white font-anton text-lg" data-testid="modal-thc-rate">{product.thc_rate || "< 0,3%"}</p>
          <p className="text-zinc-600 text-[10px]">Seuil légal UE</p>
        </div>
        {product.culture_type && (
          <div className="bg-ink-surface border border-zinc-800 p-2.5">
            <p className="text-zinc-500 text-[10px] uppercase font-bold">Type de culture</p>
            <p className="text-white text-sm font-bold" data-testid="modal-culture">🌱 {product.culture_type}</p>
          </div>
        )}
        {product.origin && (
          <div className="bg-ink-surface border border-zinc-800 p-2.5">
            <p className="text-zinc-500 text-[10px] uppercase font-bold">Origine récolte</p>
            <p className="text-white text-sm font-bold" data-testid="modal-origin">📍 {product.origin}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function EffectsBars({ effects }) {
  return (
    <div className="border border-zinc-800 bg-ink p-3" data-testid="modal-effects">
      <p className="text-neon-green text-xs uppercase font-bold mb-2">Effets ressentis</p>
      <div className="space-y-2">
        {effects.map(([label, v]) => (
          <div key={label} className="flex items-center gap-3">
            <span className="text-zinc-400 text-xs w-20">{label}</span>
            <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden">
              <div className={`h-full ${label === "Sommeil" ? "bg-neon-magenta" : "bg-neon-green"}`} style={{ width: `${Math.min(Math.max(Number(v), 0), 10) * 10}%` }} />
            </div>
            <span className="text-zinc-500 text-xs w-8 text-right">{v}/10</span>
          </div>
        ))}
      </div>
    </div>
  );
}
